import type { UngroupedStatementSchema } from "@wayflyer/customer-bankconnect";
import { useEffect, useState } from "react";

import { POLL_TIMEOUT_MS } from "./use-bank-statement-extraction-statuses";

// A statement stuck in "created" means WFE hasn't picked it up at all, so it gets a much
// shorter timeout than one already "processing" - that's confirmed as actively being worked.
export const CREATED_TIMEOUT_MS = 15 * 1000;

// uploadedAt is stamped when the presigned upload URL is created, before the file has even
// reached S3, so it can't anchor either timeout: a slow upload would already be most of the way
// to timing out before extraction is even requested. uploadConfirmedAt (set once, when the upload
// is confirmed on S3, and never touched again) anchors both timeouts instead - "created" gets the
// short window, "processing" the longer one, both measured from the same confirm-time clock
// rather than from whenever WFE happened to pick the extraction up. That deliberately doesn't
// hand a retried extraction a fresh processing window each time: unlike an extraction-side
// timestamp, which WFE would restamp on every retry, uploadConfirmedAt can never move, so a
// statement stuck retrying still times out on schedule instead of resetting indefinitely. If
// uploadConfirmedAt isn't there yet (eg. a page reload while an upload is still mid-flight
// elsewhere), we don't have a trustworthy signal at all, so treat the statement as not timed out
// rather than guessing from uploadedAt.
const extractionTimeoutDeadline = (statement: UngroupedStatementSchema): number => {
  if (statement.uploadConfirmedAt === null) {
    return Infinity;
  }
  const timeoutMs = statement.extractionState === "created" ? CREATED_TIMEOUT_MS : POLL_TIMEOUT_MS;
  return new Date(statement.uploadConfirmedAt).getTime() + timeoutMs;
};

export const hasExtractionTimedOut = (statement: UngroupedStatementSchema, now: number): boolean =>
  now >= extractionTimeoutDeadline(statement);

export const useExtractionTimeoutClock = (statements: UngroupedStatementSchema[]): number => {
  const [now, setNow] = useState(() => Date.now());
  const nextDeadline = statements
    .filter((statement) => statement.extractionState === "created" || statement.extractionState === "processing")
    .map(extractionTimeoutDeadline)
    .filter((deadline) => deadline > now)
    .reduce((earliest, deadline) => Math.min(earliest, deadline), Infinity);

  useEffect(() => {
    if (!Number.isFinite(nextDeadline)) {
      return;
    }
    const timeoutId = setTimeout(() => setNow(Date.now()), nextDeadline - Date.now());
    return () => clearTimeout(timeoutId);
  }, [nextDeadline]);

  return now;
};
